/**
 * SOLUCION SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con Consecuencias:
 * - Fondo: #f7f9fb (gris claro profesional)
 * - Colores: #0b1220, #6b7280, #0b63c6
 * - Sin gradientes decorativos
 * - Animaciones reveal con Intersection Observer
 * - Dashboard mockup realista tipo SaaS moderno
 * - Sin emojis, solo iconos Lucide
 */

import React, { useRef, useEffect } from 'react';
import { 
  CheckCircle2, 
  Zap,
  TrendingUp,
  Shield,
  Smartphone,
  BarChart3,
  Bell,
  Users,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Package,
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── BenefitCard Component ────────────────────────────────────────────────────
const BenefitCard = ({ icon, titulo, descripcion, metrica, featured, delay }) => {
  const ref = useReveal(delay);

  return (
    <div
      ref={ref}
      className={`
        relative flex flex-col gap-3.5 bg-white rounded-lg p-6 
        border transition-all duration-300
        ${featured 
          ? 'border-[#0b63c6] shadow-[0_10px_30px_rgba(11,99,198,0.12)]' 
          : 'border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] hover:border-[#0b63c6]/40 hover:shadow-[0_10px_30px_rgba(11,99,198,0.08)]'
        }
      `}
    >
      {/* Top bar si es featured */}
      {featured && (
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#0b63c6] to-[#0891b2] rounded-t-lg" />
      )}

      {/* Header: Icon + Métrica */}
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          {React.cloneElement(icon, { 
            size: 20, 
            strokeWidth: 1.75, 
            className: 'text-[#0b63c6]' 
          })}
        </div>
        <div className="px-2.5 py-1 rounded-md bg-blue-50 border border-blue-200">
          <span className="text-[11px] font-semibold text-[#0b63c6] uppercase tracking-wide">
            {metrica}
          </span>
        </div>
      </div>

      {/* Content */}
      <div>
        <h3 className="text-[15px] font-semibold text-[#0b1220] leading-snug mb-2">
          {titulo}
        </h3>
        <p className="text-[13.5px] text-[#6b7280] leading-[1.65]">
          {descripcion}
        </p>
      </div>
    </div>
  );
};

// ─── ModuleCard Component ─────────────────────────────────────────────────────
const ModuleCard = ({ nombre, items, delay }) => {
  const ref = useReveal(delay);

  return (
    <div
      ref={ref}
      className="flex flex-col gap-3 bg-white rounded-lg p-5 border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] hover:border-[#0b63c6]/40 hover:shadow-[0_10px_30px_rgba(11,99,198,0.08)] transition-all duration-300"
    >
      {/* Header */}
      <div className="flex items-center gap-2 pb-2 border-b border-black/[0.06]">
        <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
          <CheckCircle2 size={16} strokeWidth={2} className="text-[#0b63c6]" />
        </div>
        <h4 className="text-[15px] font-semibold text-[#0b1220]">{nombre}</h4>
      </div>

      {/* Items */}
      <ul className="flex flex-col gap-2">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <CheckCircle2 size={14} strokeWidth={2} className="text-emerald-500 shrink-0 mt-0.5" />
            <span className="text-[13.5px] text-[#6b7280] leading-snug">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Solucion = () => {
  const headerRef = useReveal(0);
  const dashboardRef = useReveal(0);
  const modulosHeaderRef = useReveal(0);
  const caracteristicasRef = useReveal(0);
  const valueRef = useReveal(0);

  const beneficiosPrincipales = [
    {
      icon: <BarChart3 />,
      titulo: "Rentabilidad por viaje en tiempo real",
      descripcion: "Sabes al instante si un viaje te dejó ganancia o pérdida. Antes de cerrar el mes, ya conoces tus números.",
      metrica: "Tiempo real",
      featured: true, // Destacado
    },
    {
      icon: <TrendingUp />,
      titulo: "Control de costos por vehículo",
      descripcion: "Combustible, mantenimiento, peajes, chofer. Cada gasto asociado al camión correcto para saber cuál es rentable y cuál no.",
      metrica: "Por unidad",
    },
    {
      icon: <Users />,
      titulo: "Clientes rentables vs no rentables",
      descripcion: "Descubre qué clientes te generan utilidad real y cuáles solo te dan volumen sin ganancia. Decide con data.",
      metrica: "Análisis",
    },
    {
      icon: <Bell />,
      titulo: "Alertas automáticas de pérdidas",
      descripcion: "El sistema te avisa cuando un viaje cierra en rojo, cuando un camión gasta más de lo normal, o cuando un cliente no es rentable.",
      metrica: "Alertas",
    }
  ];

  const caracteristicas = [
    {
      icon: <Smartphone />,
      titulo: "Fácil de usar",
      descripcion: "No necesitas ser contador. Interfaz simple y clara."
    },
    {
      icon: <Zap />,
      titulo: "Implementación rápida",
      descripcion: "Empiezas a ver resultados desde la primera semana."
    },
    {
      icon: <Shield />,
      titulo: "Adaptado a Perú",
      descripcion: "IGV, retenciones, percepciones. Todo configurado."
    },
    {
      icon: <DollarSign />,
      titulo: "ROI inmediato",
      descripcion: "Se paga solo evitando un par de viajes no rentables."
    }
  ];

  const modulos = [
    {
      nombre: "Viajes",
      items: ["Ingreso de viajes", "Costos asociados", "Rentabilidad calculada", "Historial completo"]
    },
    {
      nombre: "Vehículos",
      items: ["Ficha por camión", "Costos operativos", "Rendimiento", "Mantenimientos"]
    },
    {
      nombre: "Clientes",
      items: ["Rentabilidad por cliente", "Frecuencia de viajes", "Deudas pendientes", "Análisis comparativo"]
    },
    {
      nombre: "Flujo de Caja",
      items: ["Ingresos vs egresos", "Proyecciones", "Cuentas por cobrar", "Control de pagos"]
    }
  ];

  return (
    <section
      id="solucion"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#f7f9fb' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header ref={headerRef} className="max-w-[720px] flex flex-col gap-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 w-fit">
            <CheckCircle2 size={14} strokeWidth={2} className="text-[#0b63c6]" />
            <span className="text-[13px] font-medium text-[#0b63c6]">La solución</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            Control de rentabilidad en tiempo real,{' '}
            <span className="text-[#0b63c6]">diseñado específicamente para transporte de carga</span>
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            No es un software genérico. Es un sistema{' '}
            <strong className="text-[#0b1220] font-semibold">especializado</strong>{' '}
            que entiende tu negocio: viajes, vehículos, choferes, combustible, peajes y todo lo que afecta tu rentabilidad.
          </p>
        </header>

        {/* ── Beneficios principales ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {beneficiosPrincipales.map((beneficio, i) => (
            <BenefitCard key={i} {...beneficio} delay={i * 60} />
          ))}
        </div>

        {/* ── Dashboard Preview ── */}
        <div
          ref={dashboardRef}
          className="bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.05)] overflow-hidden"
        >
          {/* Header */}
          <div className="flex flex-col gap-2 px-6 py-5 bg-gradient-to-r from-[#0b1220] to-[#1e293b] border-b border-white/10">
            <h3 className="text-[15px] font-semibold text-white leading-tight">
              Vista del sistema
            </h3>
            <p className="text-[13px] text-white/60">
              Interface limpia y fácil de entender
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-6 pt-4 border-b border-black/[0.06]">
            {['Dashboard', 'Viajes', 'Vehículos', 'Clientes'].map((tab, i) => (
              <button
                key={tab}
                className={`
                  px-4 py-2.5 text-[13.5px] font-medium rounded-t-lg transition-colors
                  ${i === 0
                    ? 'text-[#0b63c6] bg-white border-t border-x border-black/[0.08] -mb-px'
                    : 'text-[#6b7280] hover:text-[#0b1220] hover:bg-gray-50'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6 bg-gradient-to-b from-white to-gray-50/50">
            {/* Métrica 1: Success */}
            <div className="flex flex-col gap-2 p-4 rounded-lg bg-white border border-emerald-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-[#6b7280] uppercase tracking-wider">
                  Rentabilidad del mes
                </span>
                <ArrowUpRight size={14} className="text-emerald-500" />
              </div>
              <div className="text-[24px] font-bold text-emerald-600 leading-tight">
                +S/ 12,450
              </div>
              <div className="text-[11px] text-[#6b7280] font-medium">
                ↑ 18% vs mes anterior
              </div>
            </div>

            {/* Métrica 2: Info */}
            <div className="flex flex-col gap-2 p-4 rounded-lg bg-white border border-blue-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-[#6b7280] uppercase tracking-wider">
                  Viajes realizados
                </span>
                <Package size={14} className="text-[#0b63c6]" />
              </div>
              <div className="text-[24px] font-bold text-[#0b1220] leading-tight">
                47
              </div>
              <div className="text-[11px] text-[#6b7280] font-medium">
                42 rentables, 5 en pérdida
              </div>
            </div>

            {/* Métrica 3: Warning */}
            <div className="flex flex-col gap-2 p-4 rounded-lg bg-white border border-amber-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-[#6b7280] uppercase tracking-wider">
                  Alertas activas
                </span>
                <Bell size={14} className="text-amber-500" />
              </div>
              <div className="text-[24px] font-bold text-amber-600 leading-tight">
                3
              </div>
              <div className="text-[11px] text-[#6b7280] font-medium">
                Requieren atención
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="px-6 pb-6">
            <div className="flex flex-col gap-4 p-5 rounded-lg bg-gradient-to-br from-gray-50 to-white border border-black/[0.06]">
              <h4 className="text-[13.5px] font-semibold text-[#0b1220]">
                Rentabilidad por vehículo (últimos 30 días)
              </h4>

              {/* Chart Bars */}
              <div className="flex items-end justify-between gap-3 h-[200px] border-b-2 border-gray-200 pb-4">
                {[
                  { label: 'VXI-789', value: '+S/ 3,200', height: '85%', positive: true },
                  { label: 'ABC-123', value: '+S/ 2,800', height: '70%', positive: true },
                  { label: 'DEF-456', value: '+S/ 2,400', height: '65%', positive: true },
                  { label: 'GHI-789', value: '-S/ 850', height: '30%', positive: false },
                  { label: 'JKL-012', value: '+S/ 2,950', height: '75%', positive: true },
                ].map((bar, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
                    <div
                      className={`
                        w-full rounded-t-md transition-all duration-300 cursor-pointer
                        flex flex-col items-center justify-start pt-2
                        ${bar.positive
                          ? 'bg-gradient-to-t from-emerald-500 to-emerald-400 hover:from-emerald-600 hover:to-emerald-500'
                          : 'bg-gradient-to-t from-red-500 to-red-400 hover:from-red-600 hover:to-red-500'
                        }
                        group-hover:shadow-lg group-hover:-translate-y-1
                      `}
                      style={{ height: bar.height, minHeight: '50px' }}
                    >
                      <span className="text-[11px] font-bold text-white">
                        {bar.value}
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-[#6b7280]">
                      {bar.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Módulos del sistema ── */}
        <div className="flex flex-col gap-6">
          <h3 ref={modulosHeaderRef} className="text-[20px] font-bold text-[#0b1220] text-center">
            Todo lo que necesitas en un solo lugar
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {modulos.map((modulo, i) => (
              <ModuleCard key={i} {...modulo} delay={i * 60} />
            ))}
          </div>
        </div>

        {/* ── Características ── */}
        <div
          ref={caracteristicasRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 rounded-xl bg-white border border-black/[0.08]"
        >
          {caracteristicas.map((car, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                {React.cloneElement(car.icon, { 
                  size: 18, 
                  strokeWidth: 1.75, 
                  className: 'text-[#0b63c6]' 
                })}
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="text-[13.5px] font-semibold text-[#0b1220]">
                  {car.titulo}
                </h4>
                <p className="text-[12.5px] text-[#6b7280] leading-[1.6]">
                  {car.descripcion}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Value Proposition ── */}
        <div
          ref={valueRef}
          className="flex flex-col lg:flex-row items-center justify-between gap-8 p-8 rounded-xl bg-gradient-to-r from-[#0b1220] to-[#1e293b] border border-white/10"
        >
          {/* Content */}
          <div className="flex flex-col gap-3 flex-1">
            <h3 className="text-[20px] font-bold text-white leading-tight">
              La diferencia está en la especialización
            </h3>
            <p className="text-[14px] text-white/80 leading-[1.65] max-w-[580px]">
              Otros sistemas son muy genéricos o muy complejos. Este está diseñado{' '}
              <strong className="text-white font-semibold">exclusivamente para empresas de transporte de carga de 2 a 10 vehículos en Perú</strong>. 
              Nada de módulos que no usarás. Solo lo que realmente necesitas para controlar tu rentabilidad.
            </p>
          </div>

          {/* Stats */}
          <div className="flex flex-col gap-3 shrink-0">
            {[
              { icon: <Clock />, number: '7 días', label: 'Implementación promedio' },
              { icon: <TrendingUp />, number: '15-25%', label: 'Mejora en rentabilidad' },
              { icon: <CheckCircle2 />, number: '100%', label: 'Visibilidad de costos' },
            ].map((stat, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              >
                <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center">
                  {React.cloneElement(stat.icon, { 
                    size: 16, 
                    strokeWidth: 2, 
                    className: 'text-white' 
                  })}
                </div>
                <div className="flex flex-col gap-0.5">
                  <div className="text-[18px] font-bold text-white leading-tight">
                    {stat.number}
                  </div>
                  <div className="text-[11px] text-white/70 font-medium">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Solucion;